import { Input, makeStyles, mergeClasses, shorthands, tokens } from '@fluentui/react-components';
import { BinRecycle20Regular, BinRecycle24Regular, Checkmark20Regular, Dismiss20Regular, Pen20Regular, Pen24Regular, Pin20Regular, Pin24Regular } from '@fluentui/react-icons';
import { FC, useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Ellipsis, Frame, LucideEllipsis, LucidePaperclip, Pin as LucidePin, PencilLine, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '../../Hooks/useResponsive';
import { EllipsisText } from '../Common/EllipsisText';
import { GptIcon } from '../Common/GptIcon';
import SyButton from '../Common/SyButton';
import { ConversationContextMenu } from '../Common/ConversationContextMenu';

interface ConversationMenuItemProps {
    title: string;
    id: string;
    selected: boolean;
    gptId: string | undefined;
    isPinned?: boolean;
    onPinToggle?: (id: string, pinned: boolean) => void;
    onRemoveClick?: (id: string) => void;
    onEditClick?: (id: string) => void;
    onTitleChange?: (id: string, title: string) => void;
    onSelect: (id: string) => void | Promise<void>;
    hidePin?: boolean;   // When true, the pin icon will be hidden. Defaults to false.
}

const selectedItemStyle = {
    backgroundColor: 'var(--menu-button-hover-bg)',
    cursor: 'pointer',
    borderRadius: '0.5rem',
};

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'row',
        position: 'relative',
        ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM),
        alignItems: 'center',
        marginLeft: '4px',
        columnGap: tokens.spacingHorizontalS,
        '&:hover': {
            backgroundColor: 'var(--menu-button-hover-bg)',
            cursor: 'pointer',
            borderRadius: '0.5rem',
        },
    },
    selected: selectedItemStyle,
    commands: {
        display: 'flex',
        flexDirection: 'row',
        columnGap: '12px',
        justifyContent: 'flex-end',
        alignItems: 'center',
    },

    icon: {
        color: tokens.colorNeutralForeground1Hover,
        '&:hover': {
            color: tokens.colorBrandForegroundInverted,
        },
        alignSelf: 'center'
    },
    lucidIcon: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: '-2px',
        color: tokens.colorNeutralForeground3,
    },
    title: {
        ...shorthands.overflow('hidden'),
        textOverflow: 'ellipsis',
        ...shorthands.flex(1),
        display: 'block',
        whiteSpace: 'nowrap',
        alignItems: 'center',
        maxWidth: '220px !important'
    },
    pinnedTitle: {
        ...shorthands.overflow('hidden'),
        textOverflow: 'ellipsis',
        ...shorthands.flex(1),
        display: 'block',
        whiteSpace: 'nowrap',
        alignItems: 'center',
        maxWidth: 'calc(220px - 24px) !important', // leave space for SyButton (Ellipsis)
    },
    deleteStyle: {
        color: 'var(--color-red-500)',
    },
    chatTitle: {
    }
});

const ConversationMenuItem: FC<ConversationMenuItemProps> = (props) => {
    const { id, onEditClick, onRemoveClick, onTitleChange, onSelect, selected, title, isPinned, hidePin } = props;
    const classes = useStyles();
    const { t } = useTranslation();
    const isMobile = useIsMobile();
    const [editMode, setEditMode] = useState(false);
    const [hovered, setHovered] = useState(false);
    const [showMenu, setShowMenu] = useState(false);
    const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);
    const [localTitle, setLocalTitle] = useState(title);
    const [isLoading, setIsLoading] = useState(false);
    const titleInputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const ellipsisButtonRef = useRef<HTMLButtonElement>(null);
    const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [isDarkMode, setIsDarkMode] = useState(false);

    // On mobile, buttons should always be visible; on desktop, only on hover
    const shouldShowButtons = isMobile || hovered;

    useEffect(() => {
        const updateDarkMode = () => {
            setIsDarkMode(document.body.getAttribute('data-theme') === 'dark');
        };
        
        updateDarkMode();
        
        const observer = new MutationObserver(updateDarkMode);
        observer.observe(document.body, { attributes: true, attributeFilter: ['data-theme'] });
        
        return () => observer.disconnect();
    }, []);

    const handleEditClick = (id: string) => {
        setEditMode(!editMode);
        if (onEditClick) {
            onEditClick(id);
        }
    };

    const handleRemoveClick = (id: string) => {
        if (onRemoveClick) {
            onRemoveClick(id);
        }
    };

    const handleDismissEdit = useCallback(() => {
        setEditMode(false);
        setLocalTitle(title);
    }, [title]);

    const handleLocalTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setLocalTitle(e.target.value);
    };

    const handleConfirmEdit = useCallback((id: string) => {
        setEditMode(false);
        if (onTitleChange) {
            onTitleChange(id, localTitle);
        }
    }, [onTitleChange, localTitle]);

    const handleDoubleClick = () => {
        // Prevent double-click from triggering edit mode - we handle this in onClick now
        return;
    };

    const handleClick = async () => {
        if (editMode || isLoading) {
            return;
        }

        // Clear any existing timeout
        if (clickTimeoutRef.current) {
            clearTimeout(clickTimeoutRef.current);
            clickTimeoutRef.current = null;
        }

        // If the item is not selected, select it first
        if (!selected) {
            try {
                setIsLoading(true);
                await onSelect(id);
            } finally {
                setIsLoading(false);
            }
        } else {
            // Item is already selected - second click should enter edit mode
            // Add a small delay to prevent conflict with double-click events
            clickTimeoutRef.current = setTimeout(() => {
                setEditMode(true);
                clickTimeoutRef.current = null;
            }, 100);
        }
    };
    useEffect(() => {
        setLocalTitle(title);
    }, [title]);

    // Handle click outside to close edit mode
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (editMode && containerRef.current && !containerRef.current.contains(event.target as Node)) {
                // Save the changes when clicking outside (only if title is not empty)
                if (localTitle.trim().length > 0 && localTitle !== '\n') {
                    handleConfirmEdit(id);
                } else {
                    handleDismissEdit();
                }
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [editMode, localTitle, id, handleConfirmEdit, handleDismissEdit]);

    useEffect(() => {
        if (editMode) {
            titleInputRef.current?.focus();
        }
    }, [editMode]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (clickTimeoutRef.current) {
                clearTimeout(clickTimeoutRef.current);
            }
        };
    }, []);

    return (
        <div
            ref={containerRef}
            className={mergeClasses(classes.root, selected ? classes.selected : '')}
            onMouseOver={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onClick={handleClick}
            onDoubleClick={handleDoubleClick}
            style={{ opacity: isLoading ? 0.6 : 1, cursor: isLoading ? 'wait' : 'pointer' }}
        >
            {!hidePin && isPinned && (
                <div className={classes.lucidIcon} aria-hidden style={{marginLeft: '-2px'}}>
                    <LucidePin size={16} />
                </div>
            )}

            {!editMode && (
                <>     
                    <div style={{ color: isDarkMode ? 'var(--color-zinc-300)' : 'inherit' }} className={mergeClasses(isPinned ? classes.pinnedTitle : classes.title, classes.chatTitle)}>
                        <EllipsisText hover={hovered} > {localTitle} </EllipsisText>
                    </div>

                    <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                        {/* Ellipsis button is still only visible on hover (or mobile) via shouldShowButtons check above; clicking toggles the menu */}
                        <button
                            aria-label="more"
                            ref={ellipsisButtonRef}
                            onClick={(ev) => {
                                // Stop propagation so parent onClick doesn't select/enter edit mode
                                ev.stopPropagation();
                                // calculate portal menu position based on button rect
                                const rect = ellipsisButtonRef.current?.getBoundingClientRect();
                                if (rect) {
                                    // position menu to the right of the button, aligned to top of button
                                    setMenuPos({ top: rect.top, left: rect.right + 8 });
                                } else {
                                    setMenuPos(null);
                                }
                                setShowMenu((s) => !s);
                            }}
                            className={classes.lucidIcon}
                            style={{ background: 'transparent', border: 'none', paddingLeft: 5, cursor: 'pointer', visibility: shouldShowButtons ? 'visible' : 'hidden' }}
                        >
                            <LucideEllipsis size={16} />
                        </button>

                        {/* Use shared ConversationContextMenu component */}
                        {showMenu && (
                            <ConversationContextMenu
                                conversationId={id}
                                isPinned={isPinned || false}
                                position={menuPos || { top: 0, left: 0 }}
                                onEdit={() => {
                                    handleEditClick(id);
                                    setShowMenu(false);
                                }}
                                onPinToggle={(convId, pinned) => {
                                    props.onPinToggle && props.onPinToggle(convId, pinned);
                                    setShowMenu(false);
                                }}
                                onDelete={(convId) => {
                                    handleRemoveClick(convId);
                                    setShowMenu(false);
                                }}
                                onClose={() => setShowMenu(false)}
                            />
                        )}
                    </div>
                </>
            )}
            {editMode && (
                <>
                    <div className="ellipsisText">
                        <input
                            ref={titleInputRef}
                            value={localTitle}
                            onChange={(e) => handleLocalTitleChange(e)}
                            onKeyUp={(e) => {
                                if (e.key === 'Enter' && e.shiftKey === false) {
                                    if (localTitle.trim().length > 0 && localTitle !== '\n') {
                                        handleConfirmEdit(id);
                                    }
                                } else if (e.key === 'Escape') {
                                    handleDismissEdit();
                                }
                            }}
                            style={{
                                border: 'none',
                                background: 'transparent',
                                outline: 'none',
                                padding: '0',
                                margin: '0',
                                fontSize: '14px',
                                lineHeight: '20px',
                                fontWeight: 400,
                                fontStyle: 'normal',
                                letterSpacing: '0.07px',
                                fontFamily: 'inherit',
                                color: 'inherit',
                                boxShadow: 'none',
                                width: '100%',
                                display: 'block',
                                textAlign: 'start',
                                verticalAlign: 'baseline',
                            }}
                        />
                    </div>

                    <Dismiss20Regular
                        className={classes.icon}
                        style={{ fontSize: '28px', width: '28px', height: '27px' }}
                        onClick={(ev) => {
                            ev.stopPropagation();
                            handleDismissEdit();
                        }}
                    />
                    <Checkmark20Regular
                        className={classes.icon}
                        style={{ fontSize: '28px', width: '28px', height: '27px' }}
                        onClick={(ev) => {
                            ev.stopPropagation();
                            handleConfirmEdit(id);
                        }}
                    />
                </>
            )}

        </div>
    );
};

export default ConversationMenuItem;
